import React, { useState } from 'react';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { DashboardSidebar } from '../components/dashboard/DashboardSidebar';
import { BookmarkIcon, CalendarIcon, MapPinIcon, BuildingIcon, EyeIcon, DownloadIcon, TrashIcon, SearchIcon, FilterIcon, StarIcon, TagIcon, ChevronDownIcon, Share2Icon, CheckIcon, XIcon, InfoIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
export function FavoritesDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenders, setSelectedTenders] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showDropdown, setShowDropdown] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  // Mock user data
  const [userData] = useState({
    name: 'FATHALLAH Mehdi',
    company: 'Tunipages',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    email: 'mehdi.fathallah@tunipages.com'
  });
  // Mock tenders data
  const tenders = [{
    id: 'tender1',
    title: 'Fourniture et installation de matériel informatique',
    organization: "Ministère de l'Éducation Nationale",
    location: 'Tunis, Tunisie',
    deadline: '2023-06-15',
    budget: '120,000 EUR',
    sector: 'IT & Télécoms',
    status: 'Ouvert',
    dateAdded: '2023-05-05',
    hasNotes: true,
    tags: ['Informatique', 'Matériel'],
    priority: 'high'
  }, {
    id: 'tender2',
    title: 'Services de maintenance des équipements réseau',
    organization: "Agence Nationale de l'Environnement",
    location: 'Sfax, Tunisie',
    deadline: '2023-06-10',
    budget: '75,000 EUR',
    sector: 'IT & Télécoms',
    status: 'Ouvert',
    dateAdded: '2023-05-03',
    hasNotes: false,
    tags: ['Réseau', 'Maintenance'],
    priority: 'medium'
  }, {
    id: 'tender3',
    title: "Développement d'une application mobile de services publics",
    organization: 'Municipalité de Tunis',
    location: 'Tunis, Tunisie',
    deadline: '2023-06-20',
    budget: '90,000 EUR',
    sector: 'IT & Télécoms',
    status: 'Ouvert',
    dateAdded: '2023-05-02',
    hasNotes: true,
    tags: ['Développement', 'Mobile'],
    priority: 'high'
  }, {
    id: 'tender4',
    title: "Mise en place d'une infrastructure cloud sécurisée",
    organization: 'Banque Centrale de Tunisie',
    location: 'Tunis, Tunisie',
    deadline: '2023-06-30',
    budget: '250,000 EUR',
    sector: 'IT & Télécoms',
    status: 'Ouvert',
    dateAdded: '2023-04-28',
    hasNotes: false,
    tags: ['Cloud', 'Sécurité'],
    priority: 'high'
  }, {
    id: 'tender5',
    title: 'Fourniture de licences logicielles Microsoft',
    organization: 'Ministère des Finances',
    location: 'Tunis, Tunisie',
    deadline: '2023-06-05',
    budget: '180,000 EUR',
    sector: 'IT & Télécoms',
    status: 'Ouvert',
    dateAdded: '2023-04-25',
    hasNotes: true,
    tags: ['Logiciels', 'Licences'],
    priority: 'medium'
  }, {
    id: 'tender6',
    title: "Installation d'un système de vidéosurveillance",
    organization: 'Aéroport International de Tunis-Carthage',
    location: 'Tunis, Tunisie',
    deadline: '2023-07-10',
    budget: '320,000 EUR',
    sector: 'Sécurité',
    status: 'Ouvert',
    dateAdded: '2023-04-20',
    hasNotes: false,
    tags: ['Vidéosurveillance', 'Sécurité'],
    priority: 'low'
  }];
  // Filter tenders based on search query
  const filteredTenders = tenders.filter(tender => tender.title.toLowerCase().includes(searchQuery.toLowerCase()) || tender.organization.toLowerCase().includes(searchQuery.toLowerCase()) || tender.location.toLowerCase().includes(searchQuery.toLowerCase()) || tender.sector.toLowerCase().includes(searchQuery.toLowerCase()) || tender.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())));
  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  // Calculate days remaining
  const getDaysRemaining = (deadlineString: string) => {
    const deadline = new Date(deadlineString);
    const today = new Date();
    const diffTime = deadline.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  // Toggle tender selection
  const toggleTenderSelection = (id: string) => {
    if (selectedTenders.includes(id)) {
      setSelectedTenders(selectedTenders.filter(tenderId => tenderId !== id));
    } else {
      setSelectedTenders([...selectedTenders, id]);
    }
  };
  // Select all tenders
  const selectAllTenders = () => {
    if (selectedTenders.length === filteredTenders.length) {
      setSelectedTenders([]);
    } else {
      setSelectedTenders(filteredTenders.map(tender => tender.id));
    }
  };
  // Handle tender action
  const handleTenderAction = (action: string, tenderId?: string) => {
    const tendersToAction = tenderId ? [tenderId] : selectedTenders;
    switch (action) {
      case 'download':
        console.log(`Downloading tenders: ${tendersToAction.join(', ')}`);
        setSuccessMessage(`Documents téléchargés pour ${tendersToAction.length} appel(s) d'offres`);
        break;
      case 'remove':
        console.log(`Removing tenders from favorites: ${tendersToAction.join(', ')}`);
        setSuccessMessage(`${tendersToAction.length} appel(s) d'offres retiré(s) des favoris`);
        setSelectedTenders([]);
        break;
      case 'share':
        console.log(`Sharing tenders: ${tendersToAction.join(', ')}`);
        setSuccessMessage(`Lien de partage créé pour ${tendersToAction.length} appel(s) d'offres`);
        break;
      default:
        break;
    }
    setShowDropdown(null);
    // Clear success message after a delay
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };
  // Get priority badge
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-2383">
            <StarIcon size={12} className="mr-1" data-id="element-2384" />
            Haute
          </span>;
      case 'medium':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-2385">
            <StarIcon size={12} className="mr-1" data-id="element-2386" />
            Moyenne
          </span>;
      case 'low':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2387">
            <StarIcon size={12} className="mr-1" data-id="element-2388" />
            Basse
          </span>;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-2389">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && <div className="fixed inset-0 bg-gray-800 bg-opacity-50 z-20 md:hidden" onClick={() => setSidebarOpen(false)} data-id="element-2390" />}
      {/* Sidebar */}
      <DashboardSidebar open={sidebarOpen} setOpen={setSidebarOpen} data-id="element-2391" />
      {/* Main content */}
      <div className="md:pl-64" data-id="element-2392">
        {/* Header */}
        <DashboardHeader user={userData} onMenuClick={() => setSidebarOpen(true)} data-id="element-2393" />
        {/* Main content */}
        <main className="py-6 px-4 sm:px-6 lg:px-8" data-id="element-2394">
          {/* Page header */}
          <div className="mb-6" data-id="element-2395">
            <div className="flex items-center mb-4" data-id="element-2396">
              <BookmarkIcon size={24} className="text-blue-600 mr-3" data-id="element-2397" />
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-2398">
                Appels d'offres favoris
              </h1>
            </div>
            <p className="text-gray-600" data-id="element-2399">
              Consultez et gérez vos appels d'offres sauvegardés
            </p>
          </div>

          {/* Success message */}
          {successMessage && <div className="mb-6 bg-green-100 border border-green-200 text-green-800 rounded-lg p-4 flex items-start" data-id="element-2400">
              <CheckIcon size={20} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2401" />
              <div data-id="element-2402">
                <p className="font-medium" data-id="element-2403">{successMessage}</p>
              </div>
              <button onClick={() => setSuccessMessage('')} className="ml-auto text-green-500 hover:text-green-700" data-id="element-2404">
                <XIcon size={16} data-id="element-2405" />
              </button>
            </div>}
          {/* Search and filters */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 mb-6" data-id="element-2406">
            <div className="p-4" data-id="element-2407">
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-2408">
                <div className="relative flex-grow" data-id="element-2409">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2410">
                    <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2411" />
                  </div>
                  <input type="text" className="block w-full bg-gray-50 border border-gray-300 rounded-md py-2 pl-10 pr-3 text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Rechercher un appel d'offres..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2412" />
                </div>
                <div data-id="element-2413">
                  <button className="w-full sm:w-auto flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" onClick={() => setShowFilters(!showFilters)} data-id="element-2414">
                    <FilterIcon size={16} className="mr-2" data-id="element-2415" />
                    Filtres
                    <ChevronDownIcon size={16} className={`ml-2 transform transition-transform ${showFilters ? 'rotate-180' : ''}`} data-id="element-2416" />
                  </button>
                </div>
              </div>

              {showFilters && <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2417">
                  <div data-id="element-2418">
                    <label htmlFor="sector" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2419">
                      Secteur
                    </label>

                    <select id="sector" name="sector" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500" defaultValue="" data-id="element-2420">
                      <option value="" data-id="element-2421">Tous les secteurs</option>

                      <option value="it" data-id="element-2422">IT & Télécoms</option>
                      <option value="construction" data-id="element-2423">BTP & Construction</option>
                      <option value="energy" data-id="element-2424">Énergie</option>
                      <option value="health" data-id="element-2425">Santé</option>
                      <option value="security" data-id="element-2426">Sécurité</option>
                    </select>
                  </div>
                  <div data-id="element-2427">
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2428">
                      Statut
                    </label>
                    <select id="status" name="status" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500" defaultValue="" data-id="element-2429">
                      <option value="" data-id="element-2430">Tous les statuts</option>
                      <option value="open" data-id="element-2431">Ouvert</option>
                      <option value="closing" data-id="element-2432">Clôture proche</option>
                      <option value="closed" data-id="element-2433">Clôturé</option>
                    </select>
                  </div>
                  <div data-id="element-2434">
                    <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2435">
                      Priorité
                    </label>
                    <select id="priority" name="priority" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500" defaultValue="" data-id="element-2436">
                      <option value="" data-id="element-2437">Toutes les priorités</option>
                      <option value="high" data-id="element-2438">Haute</option>
                      <option value="medium" data-id="element-2439">Moyenne</option>
                      <option value="low" data-id="element-2440">Basse</option>
                    </select>
                  </div>
                  <div className="md:col-span-3 flex justify-end space-x-3" data-id="element-2441">
                    <Button variant="outline" size="sm" data-id="element-2442">
                      Réinitialiser
                    </Button>
                    <Button variant="primary" size="sm" className="shadow-sm shadow-yellow-500/20" data-id="element-2443">
                      Appliquer
                    </Button>
                  </div>
                </div>}
            </div>
          </div>
          {/* Tenders list */}
          {filteredTenders.length === 0 ? <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 p-8 text-center" data-id="element-2444">
              <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mb-4" data-id="element-2445">
                <BookmarkIcon size={32} data-id="element-2446" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-2447">
                Aucun appel d'offres favori trouvé
              </h3>

              <p className="text-gray-500 max-w-md mx-auto mb-6" data-id="element-2448">
                {searchQuery ? "Essayez avec d'autres termes de recherche ou modifiez vos filtres" : "Ajoutez des appels d'offres à vos favoris pour les retrouver facilement ici"}
              </p>
              <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50" onClick={() => window.location.href = '/recherche'} data-id="element-2449">
                Rechercher des appels d'offres
              </Button>
            </div> : <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100" data-id="element-2450">
              <div className="overflow-x-auto" data-id="element-2451">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-2452">
                  <thead className="bg-gray-50" data-id="element-2453">
                    <tr data-id="element-2454">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8" data-id="element-2455">
                        <div className="flex items-center" data-id="element-2456">
                          <input type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" checked={selectedTenders.length === filteredTenders.length && filteredTenders.length > 0} onChange={selectAllTenders} data-id="element-2457" />
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2458">
                        Appel d'offres
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2459">
                        Organisation
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2460">
                        Date limite
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2461">
                        Budget
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2462">
                        Priorité
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2463">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-2464">
                    {filteredTenders.map(tender => <tr key={tender.id} className="hover:bg-gray-50" data-id="element-2465">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2466">
                          <input type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" checked={selectedTenders.includes(tender.id)} onChange={() => toggleTenderSelection(tender.id)} data-id="element-2467" />
                        </td>
                        <td className="px-6 py-4" data-id="element-2468">
                          <div className="flex flex-col" data-id="element-2469">
                            <div className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer" data-id="element-2470">
                              {tender.title}
                            </div>
                            <div className="flex items-center mt-1" data-id="element-2471">
                              <div className="flex items-center text-xs text-gray-500 mr-3" data-id="element-2472">
                                <MapPinIcon size={12} className="mr-1" data-id="element-2473" />
                                {tender.location}
                              </div>
                              <div className="flex items-center text-xs text-gray-500" data-id="element-2474">
                                <TagIcon size={12} className="mr-1" data-id="element-2475" />
                                {tender.sector}
                              </div>
                            </div>
                            <div className="flex flex-wrap mt-1.5 gap-1.5" data-id="element-2476">
                              {tender.tags.map((tag, index) => <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2477">
                                  {tag}
                                </span>)}
                              {tender.hasNotes && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2478">
                                  <InfoIcon size={12} className="mr-1" data-id="element-2479" />
                                  Notes
                                </span>}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2480">
                          <div className="flex items-center" data-id="element-2481">
                            <div className="flex-shrink-0 mr-3" data-id="element-2482">
                              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-2483">
                                <BuildingIcon size={16} className="text-gray-500" data-id="element-2484" />
                              </div>
                            </div>
                            <div className="text-sm text-gray-900" data-id="element-2485">
                              {tender.organization}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2486">
                          <div className="flex flex-col" data-id="element-2487">
                            <div className="text-sm text-gray-900" data-id="element-2488">
                              {formatDate(tender.deadline)}
                            </div>
                            {getDaysRemaining(tender.deadline) > 0 ? <div className="text-xs text-gray-500" data-id="element-2489">
                                {getDaysRemaining(tender.deadline)} jour
                                {getDaysRemaining(tender.deadline) > 1 ? 's' : ''}{' '}
                                restant
                                {getDaysRemaining(tender.deadline) > 1 ? 's' : ''}
                              </div> : <div className="text-xs text-red-500" data-id="element-2490">Expiré</div>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2491">
                          <div className="text-sm text-gray-900" data-id="element-2492">
                            {tender.budget}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2493">
                          {getPriorityBadge(tender.priority)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2494">
                          <div className="flex items-center space-x-2" data-id="element-2495">
                            <button className="text-gray-500 hover:text-blue-600" title="Voir les détails" data-id="element-2496">
                              <EyeIcon size={16} data-id="element-2497" />
                            </button>
                            <button className="text-gray-500 hover:text-blue-600" title="Télécharger" onClick={() => handleTenderAction('download', tender.id)} data-id="element-2498">
                              <DownloadIcon size={16} data-id="element-2499" />
                            </button>
                            <button className="text-gray-500 hover:text-blue-600" title="Partager" onClick={() => handleTenderAction('share', tender.id)} data-id="element-2500">
                              <Share2Icon size={16} data-id="element-2501" />
                            </button>
                            <div className="relative" data-id="element-2502">
                              <button className="text-gray-500 hover:text-blue-600" onClick={() => setShowDropdown(showDropdown === tender.id ? null : tender.id)} data-id="element-2503">
                                <ChevronDownIcon size={16} data-id="element-2504" />
                              </button>
                              {showDropdown === tender.id && <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-2505">
                                  <div className="py-1" data-id="element-2506">
                                    <button className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left" onClick={() => window.location.href = `/appel-offres/${tender.id}`} data-id="element-2507">
                                      <EyeIcon size={16} className="mr-3 text-gray-500" data-id="element-2508" />
                                      Voir les détails
                                    </button>
                                    <button className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left" onClick={() => handleTenderAction('download', tender.id)} data-id="element-2509">
                                      <DownloadIcon size={16} className="mr-3 text-gray-500" data-id="element-2510" />
                                      Télécharger
                                    </button>
                                    <button className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left" onClick={() => handleTenderAction('share', tender.id)} data-id="element-2511">
                                      <Share2Icon size={16} className="mr-3 text-gray-500" data-id="element-2512" />
                                      Partager
                                    </button>
                                    <button className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left" onClick={() => handleTenderAction('remove', tender.id)} data-id="element-2513">
                                      <TrashIcon size={16} className="mr-3 text-red-500" data-id="element-2514" />
                                      Retirer des favoris
                                    </button>
                                  </div>
                                </div>}
                            </div>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
              {/* Bulk actions */}
              {selectedTenders.length > 0 && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-2515">
                  <div className="flex items-center justify-between" data-id="element-2516">
                    <div className="text-sm text-gray-700" data-id="element-2517">
                      {selectedTenders.length} appel
                      {selectedTenders.length > 1 ? 's' : ''} d'offres
                      sélectionné{selectedTenders.length > 1 ? 's' : ''}
                    </div>
                    <div className="flex space-x-2" data-id="element-2518">
                      <Button variant="outline" size="sm" className="flex items-center" onClick={() => handleTenderAction('download')} data-id="element-2519">
                        <DownloadIcon size={14} className="mr-1" data-id="element-2520" />
                        Télécharger
                      </Button>
                      <Button variant="outline" size="sm" className="flex items-center" onClick={() => handleTenderAction('share')} data-id="element-2521">
                        <Share2Icon size={14} className="mr-1" data-id="element-2522" />
                        Partager
                      </Button>
                      <Button variant="outline" size="sm" className="flex items-center text-red-600 border-red-600 hover:bg-red-50" onClick={() => handleTenderAction('remove')} data-id="element-2523">
                        <TrashIcon size={14} className="mr-1" data-id="element-2524" />
                        Retirer des favoris
                      </Button>
                    </div>
                  </div>
                </div>}
            </div>}
        </main>
      </div>
    </div>;
}